import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DesktopDevice} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/DesktopDevice';
import {DeviceOrientation} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/DeviceOrientation';
import {DeviceType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/DeviceType';
import {MobileDevice} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/MobileDevice';
import {TabletDevice} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/TabletDevice';
import {ViewportLayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/viewportLayout/ViewportLayoutModel';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons} from '@mintdata-wsp/resources/icons/WSPIcons';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {Shortcuts} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/KeyboardController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IContextMenuItem} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const styles = require('./TopMenu.less');
const cx = classnames.bind(styles);

export abstract class ViewMenuItems
{
    public static build():IContextMenuItem[]
    {
        return [
            this.buildPreviewModeMenuItem(),
            this.buildZenModeMenuItem(),
            {
                isSeparator: true
            },
            this.buildFullMenuModeMenuItem(),
            {
                isSeparator: true
            },
            this.buildGoToMenuItem(),
            this.buildFlowEditorMenuItem(),
            {
                isSeparator: true
            },
            ...this.buildPanelsMenuItems(),
            {
                isSeparator: true
            },
            this.buildShowAllPanelsMenuItem(),
            this.buildHideAllPanelsMenuItem(),
            {
                isSeparator: true
            },
            this.buildStyleBarMenuItem(),
            {
                isSeparator: true
            },
            this.buildGridlinesMenuItem(),
            {
                isSeparator: true
            },
            ...this.buildZoomMenuItems(),
            {
                isSeparator: true
            },
            this.buildViewportSizesMenuItem()
        ];
    }

    public static buildPreviewModeMenuItem():IContextMenuItem
    {
        const {surfaceArea, keyboard} = controllers.ui;

        const isInPreviewMode:boolean = surfaceArea.isInPreviewMode();

        const label = `${isInPreviewMode ? 'Exit' : 'Enter'} preview mode`;

        return {
            label,
            rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_PREVIEW_MODE),
            icon: <WSPIcon className={cx('icon')} name={WSPIcons.visualDesigner.topBar.Preview} />,
            handler: () => surfaceArea.togglePreviewMode()
        };
    }

    public static buildZenModeMenuItem():IContextMenuItem
    {
        const {surfaceArea, keyboard} = controllers.ui;

        const isInZenPreviewMode:boolean = surfaceArea.isInZenPreviewMode();

        const label = `${isInZenPreviewMode ? 'Exit' : 'Enter'} zen mode`;

        return {
            label,
            rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_ZEN_PREVIEW_MODE),
            handler: () => surfaceArea.toggleZenPreviewMode()
        };
    }

    public static buildFullMenuModeMenuItem():IContextMenuItem
    {
        const {topMenu, keyboard} = controllers.ui;

        const isTopMenuExpanded:boolean = topMenu.isTopMenuExpanded();

        const label = `${isTopMenuExpanded ? 'Minimal' : 'Full'} toolbar`;

        return {
            label,
            rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_TOOLBAR),
            handler: () => topMenu.setTopMenuIsExpanded(!topMenu.isTopMenuExpanded())
        };
    }

    public static buildGoToMenuItem():IContextMenuItem
    {
        return {
            label: 'Go to...',
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_COMMAND_MENU),
            handler: () => controllers.ui.commandMenu.toggleCommandMenuVisibility()
        };
    }

    public static buildFlowEditorMenuItem():IContextMenuItem
    {
        return {
            label: 'Flow editor',
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.DESIGNER_OPEN_FLOW_EDITOR),
            handler: controllers.ui.topMenu.flowEditorHandler
        };
    }

    public static buildPanelsMenuItems():IContextMenuItem[]
    {
        const {keyboard, workbenchVisibility} = controllers.ui;

        return [
            {
                label: 'Outline panel',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_PAGES_PANEL),
                isSelected: workbenchVisibility.isPagesPanelVisible(),
                handler: () => workbenchVisibility.togglePagesPanelVisibility()
            },
            {
                label: 'Property panel',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_PROPERTIES_PANEL),
                isSelected: workbenchVisibility.isPropertiesPanelVisible(),
                handler: () => workbenchVisibility.togglePropertiesPanelVisibility()
            },
            {
                label: 'Spreadsheet',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_SPREADSHEET),
                isSelected: workbenchVisibility.isSpreadsheetVisible(),
                handler: () => workbenchVisibility.toggleSpreadsheetVisibility()
            },
            {
                label: 'Full width spreadsheet',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_SPREADSHEET_FULLSCREEN),
                isSelected: workbenchVisibility.isSpreadsheetFullScreen(),
                handler: () => workbenchVisibility.toggleSpreadsheetFullScreen()
            }
        ];
    }

    public static buildShowAllPanelsMenuItem():IContextMenuItem
    {
        return {
            label: 'Show all panels',
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.DESIGNER_SHOW_ALL_PANELS),
            handler: controllers.ui.topMenu.showAllPanelsHandler
        };
    }

    public static buildHideAllPanelsMenuItem():IContextMenuItem
    {
        return {
            label: 'Hide all panels',
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.DESIGNER_HIDE_ALL_PANELS),
            handler: controllers.ui.topMenu.hideAllPanelsHandler
        };
    }

    public static buildStyleBarMenuItem():IContextMenuItem
    {
        return {
            label: 'Stylebar',
            handler: () => controllers.ui.spreadsheets.cellSettingsBar.toggleBarVisibility(),
            isSelected: rootState.ui.visualDesigner.workbench.cellSettingsBar.isBarVisible
        };
    }

    public static buildGridlinesMenuItem():IContextMenuItem
    {
        const {main, workbook, mesh} = controllers.ui.spreadsheets;

        const ctx:IWSPMeshContext = workbook.getMeshContextBySheetId(main.getActiveSheet().id);
        const meshState = mesh.getState(ctx);

        const label = `${!meshState.isGridlinesVisible ? 'Show' : 'Hide'} gridlines`;

        return {
            label,
            rightLabel: controllers.ui.keyboard.getShortcutLabel(Shortcuts.DESIGNER_TOGGLE_GRID_LINES),
            handler: controllers.ui.topMenu.toggleGridlinesHandler
        };
    }

    public static buildZoomMenuItems():IContextMenuItem[]
    {
        const {keyboard, topMenu} = controllers.ui;

        return [
            {
                label: 'Zoom in',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_IN),
                isDisabled: false,
                handler: topMenu.zoomInHandler
            },
            {
                label: 'Zoom out',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_OUT),
                isDisabled: false,
                handler: topMenu.zoomOutHandler
            },
            {
                label: 'Zoom to',
                dataSource: this._buildZoomToMenuItems()
            }
        ];
    }

    private static _buildZoomToMenuItems():IContextMenuItem[]
    {
        const {keyboard, topMenu} = controllers.ui;

        return [
            {
                label: 'Zoom to 100%',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_RESET_ZOOM_AND_PAN),
                handler: topMenu.resetZoomAndPanHandler
            },
            {
                label: 'Zoom to fit',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_TO_FIT),
                handler: topMenu.zoomToFitHandler
            },
            {
                isSeparator: true
            },
            {
                label: 'Zoom to 200%',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_TO_200),
                handler: topMenu.getZoomPercentHandler(200)
            },
            {
                label: 'Zoom to 50%',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_TO_50),
                handler: topMenu.getZoomPercentHandler(50)
            },
            {
                label: 'Zoom to 25%',
                rightLabel: keyboard.getShortcutLabel(Shortcuts.DESIGNER_ZOOM_TO_25),
                handler: topMenu.getZoomPercentHandler(25)
            }
        ];
    }

    public static buildViewportSizesMenuItem():IContextMenuItem
    {
        return {
            label: 'Viewport sizes',
            dataSource: [
                {
                    label: 'Desktop',
                    dataSource: this._buildViewportDeviceSizes(DeviceType.desktop())
                },
                {
                    label: 'Tablet',
                    dataSource: this._buildViewportDeviceSizes(DeviceType.tablet())
                },
                {
                    label: 'Mobile',
                    dataSource: this._buildViewportDeviceSizes(DeviceType.mobile())
                }
            ]
        };
    }

    private static _buildViewportDeviceSizes(deviceType:DeviceType):IContextMenuItem[]
    {
        const currentLayerPage:Page = controllers.ui.surfaceArea.getCurrentLayerPage();

        if( !currentLayerPage )
            return [];

        const {viewport} = currentLayerPage.artboard;

        if( !viewport.component.ref.isResolved )
            return [];

        const {componentDesc} = viewport;

        const width:number = componentDesc.layoutWidthProp.value.value.value;
        const height:number = componentDesc.layoutHeightProp.value.value.value;

        const deviceOrientation:DeviceOrientation = width <= height
            ? DeviceOrientation.vertical()
            : DeviceOrientation.horizontal();

        const surfaceState:SurfaceState = controllers.ui.surfaceArea.getState();

        const prop:Prop<ViewportLayoutModel> = surfaceState.propsByName.get(SystemPropsConfigs.VIEWPORT_LAYOUT_PROP_NAME);

        let deviceAvailableValues:AString[];
        let getSize:(value:AString, orientation:DeviceOrientation) => {width:number; height:number};
        let devicePropPathKey:string;

        if( DeviceType.desktop().isEqualTo(deviceType) )
        {
            deviceAvailableValues = DesktopDevice.availableValues;
            getSize = DesktopDevice.getSize;
            devicePropPathKey = 'desktopDevice.value';
        }
        else if( DeviceType.tablet().isEqualTo(deviceType) )
        {
            deviceAvailableValues = TabletDevice.availableValues;
            getSize = TabletDevice.getSize;
            devicePropPathKey = 'tabletDevice.value';
        }
        else if( DeviceType.mobile().isEqualTo(deviceType) )
        {
            deviceAvailableValues = MobileDevice.availableValues;
            getSize = MobileDevice.getSize;
            devicePropPathKey = 'mobileDevice.value';
        }
        else
            return [];

        const deviceValue:AString = prop.getPropValue(PropPath.fromString(devicePropPathKey));

        return deviceAvailableValues.map((value:AString) =>
        {
            const computedSize:{width:number; height:number} = getSize(value, deviceOrientation);

            return {
                label: value.value,
                rightLabel: <span>{computedSize.width} &times; {computedSize.height}</span>,
                isSelected: deviceValue.isEqualTo(value) && computedSize.width === width && computedSize.height === height,
                handler: () =>
                {
                    prop.setPropValue(PropPath.fromString('deviceType'), deviceType);
                    prop.setPropValue(PropPath.fromString(devicePropPathKey), value);
                    componentDesc.layoutWidthProp.setInnerValue(Units.fromPixels(computedSize.width));
                    componentDesc.layoutHeightProp.setInnerValue(Units.fromPixels(computedSize.height));
                }
            };
        });
    }
}
